import { Entity } from "types/Models/entity";
import { emptyApi } from "./emptyApi";
import { BillingInvoice } from "types/Models/billing";
import { Subscription } from "types/Models/subscription";
import * as qs from "qs";
import {
  FOREIGN_QUALIFICATION,
  FORM_1099_AND_W_SERIES,
  FORM_5471_FOREIGN_SUBSIDIARY_REPORTING,
  FORM_5472_REPORTABLE_TRANSACTIONS,
  PRO_MONTHLY_BOOKKEEPING,
  STATE_ANNUAL_REPORTS,
  STATE_FRANCHISE_TAX_DELAWARE,
  US_BOOKKEEPING_ONE_TIME,
} from "types/Models/services";
import { CART_ITEM_STATUS } from "constants/cart";
import { CartItemDeletionReason } from "types/contants/cartItemDeletionReasonTypes";
import { DONT_NEED, FILED_EXTERNALLY } from "constants/cartItemDeletionReasons";
import { ACCOUNTING_METHOD_TYPES } from "constants/bookkeeping";

export type FilingState = {
  state_id: string;
  name: string;
  country: string;
  price?: string | null;
  priority_price?: string | null;
};

type Discount = {
  coupon?: {
    uuid: string;
    coupon_code: string;
  };
  discount_value: number;
};

type CartItemStatus = keyof typeof CART_ITEM_STATUS;

export type CartItem = {
  uuid: string;
  quantity: number;
  unit_price: number;
  subscription?: Subscription;
  discounted_price?: number;
  after_discount_unit_price?: number;
  product_details: {
    subscription_type?: string;
    from_date?: string;
    to_date?: string;
    product_id: string;
    product_content_type_id: string;
    product_name: string;
    quarter?: string;
    company_entity?: Pick<Entity, "country" | "country_code" | "name">;
    season?: string;
    has_balancing_payment?: boolean;
    product_description: string;
    slabs: string;
    state?: FilingState;
    base_task_key?:
      | typeof PRO_MONTHLY_BOOKKEEPING
      | typeof FORM_1099_AND_W_SERIES
      | typeof US_BOOKKEEPING_ONE_TIME
      | typeof STATE_FRANCHISE_TAX_DELAWARE
      | typeof FORM_5471_FOREIGN_SUBSIDIARY_REPORTING
      | typeof FORM_5472_REPORTABLE_TRANSACTIONS
      | typeof STATE_ANNUAL_REPORTS
      | typeof FOREIGN_QUALIFICATION;
    product_info?: string;
  };
  ra_state_name?: string;
  sales_tax_state_name?: string;
  accounting_method: ACCOUNTING_METHOD_TYPES;
  tier_information?: {
    uuid: string;
    range: string;
    amount: string;
  };
  subtotal: number;
  total: number;
  status: CartItemStatus;
  discount?: Discount;
  type: string;
  from_date?: string;
  delete_reason: typeof DONT_NEED | typeof FILED_EXTERNALLY;
  parent_template: {
    parent_template_id: string;
    parent_template_label: string;
  } | null;
};

export type Cart = {
  uuid: string;
  group_id: string;
  entity_id: string;
  cart_items: CartItem[];
  order_summary: {
    subtotal: number;
    items_count: number;
    discount?: Discount;
    total: number;
    total_due_today?: number;
  };
  total_subscription_credit_amount: number;
};

export type YearlyAdvanceTaxMetadata = { tooltip: string; deadline: string }[];
export type QuarterlyAdvanceTaxMetadata = {
  price: number;
  value: string;
  deadline: string;
}[];

export type BundleToMetadata = {
  base_task_template_id: string;
  type: string;
  title: string;
  label: string;
};

export type Product = {
  uuid: string;
  title: string;
  category: string;
  product_type: string;
  product_content_type_id: 229;
  quantity_type: string;
  description: string;
  display_text: string;
  approx_price: string;
  approx_deadline: string;
  default_task_template?: string;
  government_fee?: string;
  seasons: number[];
  share_data: {
    is_sharable: boolean;
  };
  states?: FilingState[];
  billing_period?: string;
  has_from_date: boolean;
  has_accounting_method: boolean;
  has_to_date: boolean;
  base_task_template_id: string;
  extension?: string;
  base_task_key?: string;
  quarters?: string[];
  metadata?:
    | YearlyAdvanceTaxMetadata
    | QuarterlyAdvanceTaxMetadata
    | { fields: BundleToMetadata[] };
};

export type ProductGroup = {
  subscription: Subscription[];
  [key: string]: Subscription[] | Product[];
};

type Products = {
  TAX: ProductGroup;
  BOOKS: ProductGroup;
  RND: ProductGroup;
  INTRA_GROUP: ProductGroup;
  SALES_TAX: ProductGroup;
  MAILROOM: ProductGroup;
};

export type CartTemplate = {
  uuid: string;
  name: string;
  description: string;
  items: Cart["cart_items"];
};

export type AddToCartPayload = {
  entity_id?: string;
  product_id: string;
  product_content_type_id: number;
  /** Links the cart item to its detected filing; without it the handler guesses. */
  alarm_id?: string;
  season?: string | null | number;
  quantity?: number;
  ra_state_name?: string;
  sales_tax_state_name?: string;
  payment_amount?: number;
  state_id?: string;
  from_date?: string;
  to_date?: string;
  accounting_method?: string;
  tier_id?: string;
  quarter?: string;
  deadline_date?: string;
};

export const productsApi = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getCart: build.query<Cart, { groupId: string; entityId: string }>({
      query: ({ groupId, entityId }) => ({
        url: `/api/inkle/billing/group/${groupId}/entity/${entityId}/cart/`,
      }),
      providesTags: (res) => (res ? [{ type: "CART" }] : []),
    }),
    addProductToCart: build.mutation<
      Cart,
      {
        groupId: string;
        entityId: string;
        payload: AddToCartPayload;
      }
    >({
      query: ({ groupId, payload, entityId }) => ({
        url: `/api/inkle/billing/group/${groupId}/entity/${entityId}/cart/`,
        body: payload,
        method: "POST",
      }),
      invalidatesTags: (res) =>
        res
          ? [
              { type: "CART" },
              { type: "TaskList" },
              { type: "STATE_REGISTRATIONS" },
            ]
          : [],
    }),
    removeProductFromCart: build.mutation<
      void,
      {
        groupId: string;
        entityId: string;
        cartItemId: string;
        reason: CartItemDeletionReason | null;
      }
    >({
      query: ({ groupId, cartItemId, entityId, reason }) => ({
        url: `/api/inkle/billing/group/${groupId}/entity/${entityId}/cart-item/${cartItemId}/`,
        method: "DELETE",
        params: {
          delete_reason: reason,
        },
      }),
      invalidatesTags: [
        { type: "CART" },
        { type: "STATE_REGISTRATIONS" },
        { type: "SALES_TAX_REGISTRATIONS" },
      ],
    }),

    checkoutCart: build.mutation<
      { invoice: BillingInvoice | null; cart_details: Cart },
      { groupId: string; cartId: string; entityId: string }
    >({
      query: ({ cartId, groupId, entityId }) => ({
        url: `/api/inkle/billing/group/${groupId}/entity/${entityId}/cart/${cartId}/checkout/`,
        method: "POST",
      }),
    }),
    getAllProducts: build.query<
      Products,
      {
        groupId: string;
        entityId: string;
        product_type?: string | null;
        search?: string | null;
      }
    >({
      query: ({ groupId, entityId, product_type, search }) => {
        const query = qs.stringify(
          {
            product_type,
            search_term: search,
          },
          { addQueryPrefix: true, skipNulls: true }
        );
        return {
          url: `/api/inkle/billing/group/${groupId}/entity/${entityId}/v2/product/${query}`,
        };
      },
      providesTags: ["PLATFORM_SUBSCRIPTION_PRODUCT"],
    }),
    getCartTemplates: build.query<
      CartTemplate[],
      { groupId: string; entityId: string }
    >({
      query: ({ groupId, entityId }) => ({
        url: `/api/inkle/billing/group/${groupId}/entity/${entityId}/cart-templates/`,
      }),
    }),

    pushToCartFromTemplate: build.mutation<
      Cart,
      { groupId: string; entityId: string; templateId: string }
    >({
      query: ({ groupId, entityId, templateId }) => ({
        url: `/api/inkle/billing/group/${groupId}/entity/${entityId}/cart-template/${templateId}/load-cart/`,
        method: "POST",
      }),
      invalidatesTags: [{ type: "CART" }],
    }),

    getCartItems: build.query<
      { cart_items: CartItem[] },
      {
        entityId: string;
        status: CartItemStatus;
      }
    >({
      query: ({ entityId, status }) => ({
        url: `/api/inkle/billing/entity/${entityId}/cart_item/?cart_item_status=${status}`,
      }),
      providesTags: [{ type: "CART" }],
    }),

    updateCartItem: build.mutation<
      {
        cart_item_id: string;
        entity_id: string;
        status: CartItemStatus;
      },
      {
        entityId: string;
        payload: { status: CartItemStatus; cartItemId: string };
      }
    >({
      query: ({ entityId, payload }) => ({
        url: `/api/inkle/billing/entity/${entityId}/cart_item/`,
        method: "PUT",
        body: {
          item_status: payload.status,
          cart_item_id: payload.cartItemId,
        },
      }),
      invalidatesTags: [{ type: "CART" }],
    }),
    sendCartToChat: build.mutation<
      { message_id: string },
      {
        groupId: string;
        entityId: string;
        channelId: string;
        message: string;
      }
    >({
      query: ({ groupId, entityId, channelId, message }) => ({
        url: `/api/inkle/billing/group/${groupId}/entity/${entityId}/cart/send_to_chat/`,
        method: "POST",
        body: {
          card_type: "CART",
          channel_url: channelId,
          message,
        },
      }),
    }),

    getDeletedCartItem: build.query<
      CartItem,
      { entityId: string; payload: AddToCartPayload }
    >({
      query: ({ entityId, payload }) => ({
        url: `/api/inkle/billing/entity/${entityId}/cart_item_info/`,
        params: payload,
      }),
    }),
    updateCartProductQuantity: build.mutation<
      Cart,
      {
        entityId: string;
        cartItemId: string;
        quantity: number;
      }
    >({
      query: ({ entityId, cartItemId, quantity }) => ({
        url: `/api/inkle/billing/entity/${entityId}/cart_item/${cartItemId}/quantity/`,
        body: { quantity },
        method: "PUT",
      }),
      invalidatesTags: (res) => (res ? [{ type: "CART" }] : []),
    }),
    updateCartItemAmount: build.mutation<
      Cart,
      {
        entityId: string;
        cartItemId: string;
        unitPrice: number;
      }
    >({
      query: ({ entityId, cartItemId, unitPrice }) => ({
        url: `/api/inkle/billing/entity/${entityId}/cart_item/${cartItemId}/amount/`,
        body: { unit_price: unitPrice },
        method: "PUT",
      }),
      invalidatesTags: (res) => (res ? [{ type: "CART" }] : []),
    }),
  }),

  overrideExisting: false,
});

export const {
  useAddProductToCartMutation,
  useGetCartQuery,
  useRemoveProductFromCartMutation,
  useCheckoutCartMutation,
  useGetAllProductsQuery,
  useGetCartTemplatesQuery,
  usePushToCartFromTemplateMutation,
  useLazyGetCartQuery,
  useGetCartItemsQuery,
  useUpdateCartItemMutation,
  useSendCartToChatMutation,
  useGetDeletedCartItemQuery,
  useUpdateCartProductQuantityMutation,
  useUpdateCartItemAmountMutation,
} = productsApi;
